import { Component, computed, inject, input } from '@angular/core';
import { AssetService } from '../../services/asset-service/asset.service';
import { Loader } from '../loader';
import { Router } from '@angular/router';
import { AssetTypeTag } from '../asset-type-tag/asset-type-tag';
import { AssetListCardDetail, AssetType } from '../../services/asset-service/types';
import { AssetDownloadButton } from '../asset-download-button';
import { AssetTrustBadge } from '../asset-trust-badge/asset-trust-badge';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { tablerDownload, tablerEye } from '@ng-icons/tabler-icons';
import { AssetLaunchButton } from '../asset-launch-button';
import { AssetAccessType } from '../asset-access-type/asset-access-type';

@Component({
  selector: 'asset-card',
  templateUrl: './asset-card.html',
  styleUrl: './asset-card.scss',
  imports: [
    Loader,
    AssetTypeTag,
    AssetDownloadButton,
    AssetTrustBadge,
    NgIcon,
    AssetLaunchButton,
    AssetAccessType,
  ],
  providers: [provideIcons({ tablerDownload, tablerEye })],
  host: {
    '(click)': 'onCardClick()',
    '[class.grid-parent]': 'isGrid()',
  },
})
export class AssetCard {
  private router = inject(Router);

  isGrid = input(false);
  asset = input.required<AssetListCardDetail>();
  ctaDisabled = input(false);
  ctaLabelOverride = input('');

  readonly id = computed(() => {
    return this.asset().asset_id;
  });
  readonly assetType = computed(() => {
    return this.asset().asset_type;
  });
  readonly title = computed(() => {
    return this.asset().asset_name;
  });
  readonly description = computed(() => {
    return this.asset().short_description;
  });
  readonly accessLevel = computed(() => {
    return this.asset().access;
  });

  regionsText = computed(() => {
    return this.asset().regions.join(', ');
  });

  downloadCountText = computed(() => {
    const count = this.asset().download_count;
    return `${count} Download${count === 1 ? '' : 's'}`;
  });

  onCardClick = () => {
    this.router.navigate(['/assets', this.id()]);
  };

  assetService = inject(AssetService);
  isBookmarked = computed(() => {
    if (!this.assetService.listAllBookmarks.hasValue()) {
      // NOTE: Just for type-safety. Button is disabled when query has no value yet.
      return false;
    }
    const bookmarkedAssetIds = this.assetService.listAllBookmarks
      .value()
      .items.map(({ asset_id }) => asset_id);

    return bookmarkedAssetIds.findIndex((id) => id === this.id()) >= 0;
  });
  handleBookmarkButtonClick = (e: Event) => {
    e.stopPropagation();
    if (!this.assetService.listAllBookmarks.hasValue()) {
      // Don't send any request if bookmarked state is unknown
      return;
    }

    if (this.isBookmarked()) {
      this.assetService.deleteBookmark(this.id);
    } else {
      this.assetService.addBookmark(this.id);
    }
  };
  isBookmarkButtonLoading = computed(() => {
    return (
      !this.assetService.listAllBookmarks.hasValue() ||
      this.assetService.listAllBookmarks.isLoading()
    );
  });

  get ctaLabel(): string {
    if (this.ctaLabelOverride) return this.ctaLabelOverride();
    const map: Record<AssetType, string> = {
      Dataset: 'Download',
      'AI Agent': 'Request API Key',
      'AI Tool': 'Request API Key',
      'AI Product': 'Launch Product',
    };
    return map[this.assetType()] ?? 'Open';
  }

  get effectiveCtaDisabled(): boolean {
    return this.ctaDisabled() || this.assetType() === 'AI Agent' || this.assetType() === 'AI Tool';
  }
}
